import { RecaptchaVerifier } from "firebase/auth";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import { useState } from "react";

import { getParams } from "../hooks";
import { generateFirestoreId, readableError } from "../utils";
import { FF, FirebaseContext } from "./index";

export interface Variables {
  [key: string]: string;
}

interface Props {
  children: React.ReactNode;
  variables?: Variables;
}

export const FirebaseProvider: React.FC<Props> = ({ children, variables }) => {
  const [confirmationResult, setConfirmationResult] =
    useState<firebase.auth.ConfirmationResult | null>(null);

  const emailForSignInKey = "emailForSignIn";

  const firebaseConfig = variables
    ? {
        apiKey: variables.FIREBASE_API_KEY,
        authDomain: variables.FIREBASE_AUTH_DOMAIN,
        projectId: variables.FIREBASE_PROJECT_ID,
        storageBucket: variables.FIREBASE_STORAGE_BUCKET,
        messagingSenderId: variables.FIREBASE_MESSAGING_SENDER_ID,
        appId: variables.FIREBASE_APP_ID,
      }
    : {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
      };

  const app = firebase.initializeApp(firebaseConfig);

  const auth = firebase.auth();

  const firestore = firebase.firestore(app);

  const storage = firebase.storage(app);

  const googleProvider = new firebase.auth.GoogleAuthProvider();
  googleProvider.setCustomParameters({ prompt: "select_account" });

  const signInWithGoogle = async () => {
    await auth.signInWithPopup(googleProvider);
  };

  const createUserWithEmailAndPassword: FF = async (...props) => {
    const { email = "", password = "" } = await getParams(...props);
    await auth.createUserWithEmailAndPassword(email, password);
  };

  const signInWithEmailAndPassword: FF = async (...props) => {
    const { email = "", password = "" } = await getParams(...props);
    await auth.signInWithEmailAndPassword(email, password);
  };

  const sendPasswordResetEmail: FF = async (...props) => {
    const { email = "" } = await getParams(...props);
    await auth
      .sendPasswordResetEmail(email)
      .then(() => alert(`Link was sent to ${email}`));
  };

  const signInWithPhoneNumber: FF = async (...props) => {
    const { phone = "" } = await getParams(...props);
    const reCAPTCHA = document.createElement("div");
    reCAPTCHA.id = "reCAPTCHA";
    reCAPTCHA.style.display = "none";
    document.body.appendChild(reCAPTCHA);
    await auth
      .signInWithPhoneNumber(
        phone,
        // @ts-ignore
        new RecaptchaVerifier(auth, reCAPTCHA.id, { size: "invisible" })
      )
      .then((res) => setConfirmationResult(res))
      .finally(() => reCAPTCHA.parentElement?.removeChild(reCAPTCHA));
  };

  const reSignInWithPhoneNumber: FF = async (...props) => {
    const result = window.confirm("Trouble getting code? Resend?");
    if (result) {
      await signInWithPhoneNumber(...props);
    }
  };

  const confirmPhoneNumber: FF = async (...props) => {
    const { code = "" } = await getParams(...props);
    if (confirmationResult) {
      await confirmationResult
        .confirm(code)
        .then(() => setConfirmationResult(null));
    }
  };

  const sendSignInLinkToEmail: FF = async (...props) => {
    const { email = "" } = await getParams(...props);
    await auth
      .sendSignInLinkToEmail(email, {
        url: window.location.origin,
        handleCodeInApp: true,
      })
      .then(() => localStorage.setItem(emailForSignInKey, email));
  };

  const reSendSignInLinkToEmail: FF = async (...props) => {
    const result = window.confirm("Trouble getting email? Resend?");
    if (result) {
      await sendSignInLinkToEmail(...props);
    }
  };

  const getAssetRecord = (file: any) =>
    new Promise(async (resolve) => {
      const { name, type } = file;
      const [resourceType, format] = type.split("/");
      const storageRef = storage.ref(
        `uploads/${auth.currentUser?.uid}/${generateFirestoreId()}/${name}`
      );
      storageRef
        .put(file)
        .then(() =>
          storageRef.getDownloadURL().then((url) =>
            resolve({
              title: null,
              folder: "~",
              url,
              resourceType,
              format,
              width: null,
              height: null,
              bytes: null,
              duration: null,
              version: null,
              signature: null,
              error: null,
            })
          )
        )
        .catch((err) => alert(readableError(err.message)))
        .finally(() => resolve(null));
    });

  return (
    <FirebaseContext.Provider
      value={{
        f: {
          auth,
          firestore,
          signInWithGoogle,
          createUserWithEmailAndPassword,
          signInWithEmailAndPassword,
          sendPasswordResetEmail,
          signInWithPhoneNumber,
          reSignInWithPhoneNumber,
          confirmPhoneNumber,
          sendSignInLinkToEmail,
          reSendSignInLinkToEmail,
          getAssetRecord,
          emailForSignInKey,
        },
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};
